import { MDBBtn, MDBCard, MDBCardBody, MDBCheckbox, MDBCol, MDBRow, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBIcon } from "mdb-react-ui-kit"

import { useTranslation } from "react-i18next"
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../../utils/sentryHelpers';
import { isoToGermanDate } from "../../../../utils/dateTools";
import { getLocalizedText } from "../../../../utils/localizedText";
import { transformMoney } from "../../../../utils/money";
import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import SideCart from "../../../order/checkout/SideCart";


export default function Booking_Confirmation({ user, event, params, taxRate, personalAddress, useProfileAddressForBilling, setOrderDiscounts, setUseProfileAddressForBilling,
  billingAddress, handleBookTicket, bookingInProgress, isProcessingCheckout, handleClickPrev, handleClickNext, goToStep, hasVisitStep, validations, errors, linkedDiscount }) {
  const client = generateClient()
  const { t, i18n } = useTranslation()

  const errorAlertRef = useRef(null);
  const [tocAccepted, setTocAccepted] = useState(false);
  const [showTacModal, setShowTacModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Discount code and handling
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [discountErrorMessages, setDiscountErrorMessages] = useState([]);

  // Scroll to error alert when errors appear
  useEffect(() => {
    if (errors?.length > 0) {
      setTimeout(() => {
        errorAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [errors]);

  // Auto-apply linked discount from invitation code
  useEffect(() => {
    if (linkedDiscount) {
      setAppliedDiscounts(prev => {
        // Don't add if already present
        if (prev.find(d => d.id === linkedDiscount.id)) return prev;
        // Remove other non-combinable discounts if needed
        if (!linkedDiscount.canBeCombined) return [linkedDiscount];
        return [...prev, linkedDiscount];
      });
    } else {
      // Remove any previously auto-applied invitation discount
      setAppliedDiscounts(prev => prev.filter(d => !d.isFromInvitation));
    }
  }, [linkedDiscount]);

  const applyDiscountCode = async () => {
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
            query DiscountCodesByCode(
            $code: String!
          ) {
            discountCodesByCode(
              code: $code
            ) {
              items {
                id
                code
                name {
                  language
                  text
                }
                description {
                  language
                  text
                }
                discountType
                discountAmount
                discountPercentage
                maxUsage
                usageCount
                startDate
                endDate
                status
                restrictedProductIds
                canBeCombined
              }
              nextToken
              __typename
            }
          }
        `,
        variables: {
          code: discountCode
        }
      });
      console.log("response", response);
      const discount = response.data.discountCodesByCode.items[0];
      if (discount) {
        // check if discount already applied
        if (appliedDiscounts.find(d => d.id === discount.id)) {
          setDiscountErrorMessages([t("Discount code already applied")]);
          return;
        } else if (discount.startDate && new Date(discount.startDate) > new Date()) {
          setDiscountErrorMessages([t("Discount code is not yet valid")]);
          return;
        } else if (discount.endDate && new Date(discount.endDate) < new Date()) {
          setDiscountErrorMessages([t("Discount code has expired")]);
          return;
        } else if (discount.maxUsage && discount.usageCount >= discount.maxUsage) {
          setDiscountErrorMessages([t("Discount code has reached maximum usage")]);
          return;
        } else {
          setDiscountErrorMessages([]);
          if (discount.canBeCombined) {
            setAppliedDiscounts([...appliedDiscounts, discount]);
          } else {
            setAppliedDiscounts([discount]);
          }
        }

      } else {
        setDiscountErrorMessages([t("Discount code not found")]);
      }
    }
    catch (error) {
      console.error("Error applying discount code", error);
      captureError(error, {
        feature: 'ticket-management',
        operation: 'apply-discount-code',
        consoleMessage: 'Error applying discount code',
        context: { discountCode }
      });
    }
  }

  const removeDiscountCode = (id) => {
    const updatedDiscounts = appliedDiscounts.filter(discount => discount.id !== id);
    setAppliedDiscounts(updatedDiscounts);
  }

  const effectiveQty = event?.ticketPricePerDay ? (params.selectedDays?.length || 1) : 1;

  const discountsWithAmounts = useMemo(() => {
    if (!event?.ticketProduct) return appliedDiscounts;

    const calculateDiscount = (discount) => {
      if (discount.discountType === "percentage") {
        const product = event.ticketProduct;
        const productTotal = product.price * effectiveQty;
        if (
          (discount.restrictedProductIds && discount.restrictedProductIds.includes(product.id)) ||
          !discount.restrictedProductIds
        ) {
          return productTotal * discount.discountPercentage / 100;
        }
        return 0;
      }
      else if (discount.discountType === "fixedAmount") {
        if (discount.restrictedProductIds) {
          return discount.restrictedProductIds.includes(event.ticketProduct.id)
            ? discount.discountAmount
            : 0;
        } else {
          return discount.discountAmount;
        }
      }
      return 0;
    };

    return appliedDiscounts.map((discount) => ({
      ...discount,
      amount: calculateDiscount(discount),
    }));
  }, [appliedDiscounts, event.ticketProduct, effectiveQty]);

  // Set discounts to order
  useEffect(() => {
    setOrderDiscounts(discountsWithAmounts);
  }, [discountsWithAmounts, setOrderDiscounts]);

  // Reusable address display
  const AddressBlock = ({ address }) => (
    <div translate="no" style={{ lineHeight: "1.6" }}>
      {address?.company && <>{address.company}<br /></>}
      {address?.title ? address.title + " " : ""}{address?.firstName} {address?.middleName} {address?.lastName}
      <br />
      {address?.street} {address?.streetNumber}
      <br />
      {address?.zip} {address?.city}
      <br />
      {address?.countryCode}
    </div>
  );

  const displayedBillingAddress = useProfileAddressForBilling ? personalAddress : billingAddress;

  return (
    event &&
    <>
      <h4 className="mb-3">{t("Confirmation")}</h4>

      <MDBRow className="g-3 g-lg-4">
        {/* Left column: Summary & Addresses */}
        <MDBCol size="12" lg="6">
          <MDBCard className="text-start h-100">
            <MDBCardBody>
              {/* Ticket */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <MDBIcon fas icon="ticket-alt" className="me-2 text-muted" />
                  <h5 className="mb-0">{t("Ticket")}</h5>
                </div>
                <MDBBtn color="link" size="sm" className="p-0" onClick={() => goToStep(1)}>
                  <MDBIcon fas icon="pen" className="me-1" />{t("Change")}
                </MDBBtn>
              </div>
              <div>
                <strong>{event.name.find(n => n.language === i18n.language)?.text || event.name[0]?.text}</strong>
                <br />
                {params.visitorType === "staff" ? t("Booth Staff Ticket") : t("Visitor-Ticket")}
                <span className="ms-2 text-muted">
                  {transformMoney(event.ticketProduct?.price * (1 + taxRate / 100))}
                  {event.ticketPricePerDay && <small> / {t("Day")}</small>}
                  <small className="ms-1">({taxRate > 0 ? t("inkl. MwSt") : t("Net (VAT exempt)")})</small>
                </span>
              </div>

              {/* Visit - days & invitation code */}
              {hasVisitStep && <>
                <hr className="my-3" />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    <MDBIcon fas icon="calendar-day" className="me-2 text-muted" />
                    <h5 className="mb-0">{t("Your visit")}</h5>
                  </div>
                  <MDBBtn color="link" size="sm" className="p-0" onClick={() => goToStep(2)}>
                    <MDBIcon fas icon="pen" className="me-1" />{t("Change")}
                  </MDBBtn>
                </div>
                {params.selectedDays?.length > 0 &&
                  <div className="mb-1">
                    <strong>{t("Days")}:</strong>{" "}
                    {[...(params.selectedDays || [])].sort().map(day => isoToGermanDate(day)).join(", ")}
                  </div>
                }
                {params.invitationCode &&
                  <div>
                    <strong>{t("Invitation code")}:</strong> {params.invitationCode}
                  </div>
                }
              </>}

              <hr className="my-3" />

              {/* Personal Information */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <MDBIcon fas icon="user" className="me-2 text-muted" />
                  <h5 className="mb-0">{t("Personal information")}</h5>
                </div>
                <MDBBtn color="link" size="sm" className="p-0" onClick={() => goToStep(hasVisitStep ? 3 : 2)}>
                  <MDBIcon fas icon="pen" className="me-1" />{t("Change")}
                </MDBBtn>
              </div>
              <AddressBlock address={personalAddress} />

              <hr className="my-3" />

              {/* Billing Address */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <MDBIcon fas icon="file-invoice" className="me-2 text-muted" />
                  <h5 className="mb-0">{t("Billing address")}</h5>
                </div>
                <MDBBtn color="link" size="sm" className="p-0" onClick={() => goToStep(hasVisitStep ? 3 : 2)}>
                  <MDBIcon fas icon="pen" className="me-1" />{t("Change")}
                </MDBBtn>
              </div>
              <AddressBlock address={displayedBillingAddress} />
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        {/* Right column: Cart */}
        <MDBCol size="12" lg="6" className="text-start">
          {event &&
            <SideCart
              taxRate={taxRate}
              customerType={"consumer"}
              showBreakdownAlways={true}
              products={[{
                name: event.name.map(n => ({ language: n.language, text: "Ticket " + n.text })),
                image: { main: { url: "https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/" + event.image?.fileName } },
                price: event.ticketProduct?.price,
                shortDescription: [{ text: [...(params.selectedDays || [])].sort().map(day => isoToGermanDate(day)).join(", ") || "" }],
                qty: effectiveQty
              }]
              }
              showDiscountCode={true}
              discountCode={null}
              setDiscountCode={setDiscountCode}
              applyDiscountCode={applyDiscountCode}
              removeDiscountCode={removeDiscountCode}
              appliedDiscounts={discountsWithAmounts}
              discountErrorMessages={discountErrorMessages}
            />
          }
        </MDBCol>
      </MDBRow>

      {/* Checkout action area */}
      <MDBRow className="mt-3 mt-lg-4">
        <MDBCol size="12" lg="8" className="mx-auto">
          <div className="text-center">
            <MDBCardBody className="d-flex flex-column gap-2 py-3">
              <div className="d-flex justify-content-center">
                <MDBCheckbox
                  id={"TaC"}
                  checked={tocAccepted}
                  onChange={e => setTocAccepted(e.target.checked)}
                  label={<>
                    {t("I accept the ")}
                    <span
                      className="link-primary"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowTacModal(true);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >{t("terms and conditions")}</span>
                    {t(" and the ")}
                    <span
                      className="link-primary"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowPrivacyModal(true);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >{t("privacy policy")}</span></>}
                />
              </div>
              <div className="mt-3" style={{ maxWidth: "500px", margin: "0 auto" }}>
                {(bookingInProgress || isProcessingCheckout) ?
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t("Loading...")}</span>
                  </div>
                  :
                  <MDBBtn disabled={!tocAccepted} color="success" className="w-100" onClick={() => handleBookTicket()}>
                    {(() => {
                      const totalDiscount = discountsWithAmounts.reduce((sum, d) => sum + (d.amount || 0), 0);
                      const effectiveTotal = (event?.ticketProduct?.price || 0) * effectiveQty - totalDiscount;
                      return effectiveTotal <= 0 ? t("Book now") : t("Place your order and pay");
                    })()}
                  </MDBBtn>
                }
              </div>
              {errors?.length > 0 && (() => {
                const fieldErrors = errors.filter(e => e.type === 'field');
                const otherErrors = errors.filter(e => e.type !== 'field');
                return (
                  <>
                    {fieldErrors.length > 0 && (
                      <div ref={errorAlertRef} className="alert alert-danger mt-3 text-start">
                        <div className="d-flex align-items-center mb-2">
                          <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                          <strong>{t("Please fill out all required fields.")}</strong>
                        </div>
                        {fieldErrors.map((error, index) => (
                          <p key={index} className="mb-1 small">{error.message}</p>
                        ))}
                      </div>
                    )}
                    {otherErrors.length > 0 && (
                      <div ref={fieldErrors.length === 0 ? errorAlertRef : null} className="alert alert-danger mt-3 text-start">
                        {otherErrors.map((error, index) => (
                          <div key={index} className="d-flex align-items-center">
                            <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                            <span>{error.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </MDBCardBody>
          </div>
        </MDBCol>
      </MDBRow>

      {/* Terms and Conditions Modal - Rendered via Portal to body */}
      {showTacModal && createPortal(
        <MDBModal open={showTacModal} onClose={() => setShowTacModal(false)}>
          <MDBModalDialog size="xl" className="text-start">
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>{t("Terms and Conditions")}</MDBModalTitle>
                <MDBBtn className='btn-close' color='none' onClick={() => setShowTacModal(false)}></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody>
                <div dangerouslySetInnerHTML={{ __html: getLocalizedText(event?.ticketTAC) }} />
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>,
        document.body
      )}

      {/* Privacy Policy Modal - Rendered via Portal to body */}
      {showPrivacyModal && createPortal(
        <MDBModal open={showPrivacyModal} onClose={() => setShowPrivacyModal(false)}>
          <MDBModalDialog size="xl" className="text-start">
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>{t("Privacy Policy")}</MDBModalTitle>
                <MDBBtn className='btn-close' color='none' onClick={() => setShowPrivacyModal(false)}></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody>
                <div dangerouslySetInnerHTML={{ __html: getLocalizedText(event?.ticketPrivacyPolicy) }} />
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>,
        document.body
      )}
    </>
  )
}